import { Search, SentimentDissatisfied } from "@mui/icons-material";
import { Typography } from "@mui/material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState, useCallback } from "react";
import { config } from "../App";
import Cart, { generateCartItemsFrom } from "./Cart";
import Footer from "./Footer";
import Header from "./Header";
import ProductCard from "./ProductCard";
import "./Products.css";
// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 * 
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */


const Products = () => {
  const { enqueueSnackbar} = useSnackbar();

  const [loader, setLoader] = useState(true);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [items,setItems]=useState([]);

  useEffect(() => {
    const fetchProductsData = async()=>{
        const products = await performAPICall();
        if(localStorage.getItem('token')){
          const {cartItems} = await getCartList();
          console.info(cartItems,"cart item");
          const cartItemsDetails = generateCartItemsFrom(cartItems, products);
          console.info(cartItemsDetails,"cart item details");
          setItems([...cartItemsDetails]);
          // updateCartItem();
          setLoader(false);
        }else{
          setLoader(false);
        }
       
    }
    fetchProductsData();
  }, []);

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Fetch products data and store it
  /**
   * Make API call to get the products list and store it to display the products
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on all available products
   *
   * API endpoint - "GET /products"
   *
   * Example for successful response from backend:
   * HTTP 200
   * [
   *      {
   *          "name": "iPhone XR",
   *          "category": "Phones",
   *          "cost": 100,
   *          "rating": 4,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "v4sLtEcMpzabRyfx"
   *      },
   *      {
   *          "name": "Basketball",
   *          "category": "Sports",
   *          "cost": 100,
   *          "rating": 5,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "upLK9JbQ4rMhTwt4"
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 500
   * {
   *      "success": false,
   *      "message": "Something went wrong. Check the backend console for more details"
   * }
   */
  const performAPICall = async () => {
    const url = `${config.endpoint}/products`;
    const response = await axios.get(url);
    if(response.data){
      setProducts([...response.data]);
      return response.data;
    }else {
      enqueueSnackbar('Something went wrong',{variant:'error'});
      return [];
    }
  };
  
  const getCartList =async ()=>{
    try{
      const url = `${config.endpoint}/cart`;
      const response = await  axios.get(url,{
         headers:{
           Authorization:`Bearer ${localStorage.getItem('token')}`
         }
       });
       if(response?.data?.status){
        const {cartData}=response.data;
         setLoader(false);
         return cartData;
       }else{
         setLoader(false);
         enqueueSnackbar('Something went wrong',{variant:'error'});
         return [];
       }
    }catch(err){
      console.error(err);
      setLoader(false);
      enqueueSnackbar('Something went wrong',{variant:'error'});
      return [];
    }
  }

  const saveCartItem =(payload)=>{
     const url = `${config.endpoint}/cart`;
     axios.post(url,payload,{
        headers:{
            Authorization:`Bearer ${localStorage.getItem('token')}`
        }
     })
     .then((response)=>{
      console.info(response,"response");
      const {status, cartData}=response?.data;
      if(status){
        const cartItems = generateCartItemsFrom(cartData?.cartItems,products);
       setItems([...cartItems]);
      }
     }).catch((err)=>{
      console.info(err,"err");
      const {data}=err.response;
      enqueueSnackbar(data.message || 'Something went wrong',{variant:'error'});
     })
  }

  const updateCartQuantity=async(payload)=>{
    const url = `${config.endpoint}/cart`;
     const response = await axios.put(url,payload,{
        headers:{
            Authorization:`Bearer ${localStorage.getItem('token')}`
        }
     });
     console.info(response,"response");
     if(response?.data?.status){
      const {cartData={}}=response.data;
      const formattedCartItems = generateCartItemsFrom(cartData?.cartItems,products);
      setItems([...formattedCartItems]);
     }else{
      enqueueSnackbar(response?.data?.message || 'Something went wrong',{variant:'error'});
     }
  };

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Implement search logic
  /**
   * Definition for search handler
   * This is the function that is called on adding new search keys
   *
   * @param {string} text
   *    Text user types in the search bar. To filter the displayed products based on this text.
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on filtered set of products
   *
   * API endpoint - "GET /products/search?value=<search-query>"
   *
   */
  const performSearch = async (text) => {
    const url = `${config.endpoint}/products/search?value=${text}`;
    axios.get(url)
      .then((response) => {
        setProducts([...response.data]);
        setLoader(false);
      }).catch((err) => {
        setLoader(false);
        setProducts([]);
      })
  };

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Optimise API calls with debounce search implementation
  /**
   * Definition for debounce handler
   * With debounce, this is the function to be called whenever the user types text in the searchbar field
   *
   * @param {{ target: { value: string } }} event
   *    JS event object emitted from the search input field
   *
   * @param {NodeJS.Timeout} debounceTimeout
   *    Timer id set for the previous debounce call
   *
   */
  const debounceSearch = (func, debounceTimeout) => {
    let timeout;

    return function innerFunc(...args) {
      const later = () => {
        timeout = null;
        setLoader(true);
        func(...args)
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, debounceTimeout);
    }
  };

  const debounceHandler = useCallback(debounceSearch((term) => {
    performSearch(term)
  }, 500), []);

  const handleSearchTerm = (e) => {
    debounceHandler(e.target.value)
    setSearchTerm(e.target.value);
  }
  
  const isItemInCart =(id)=>{
   let available = false;
   items.forEach((item)=>{
    if(item.productId === id){
      available = true;
    }
   });
   return available;
  };

  const handleAddToCart =(product)=>{
    if(!localStorage.getItem('token')){
      enqueueSnackbar('Login to add an item to the Cart',{variant:'warning'});
    }else if(isItemInCart(product._id)){
      enqueueSnackbar('Item already in cart. Use the cart sidebar to update quantity or remove item.',{variant:'warning'});
    }else{
      const payload ={productId: product._id,quantity:1};
      saveCartItem(payload);
    }
  }
  
  const handleQuantity =(type = 'add',product)=>{
  console.info(product,"product");
  if(type === 'add'){
    const payload ={productId: product._id,quantity:product.quantity + 1};
    updateCartQuantity(payload);
  }else{
    const payload ={productId: product._id,quantity:product.quantity - 1};
    updateCartQuantity(payload);
  }
  }

  return (
    <div>
      <Header>
        {/* TODO: CRIO_TASK_MODULE_PRODUCTS - Display search bar in the header for Products page */}
        <TextField
          className="search-desktop"
          size="small"
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search color="primary" />
              </InputAdornment>
            ),
          }}
          placeholder="Search for items/categories"
          name="search"
          value={searchTerm}
          onChange={handleSearchTerm}
        />
      </Header>

      {/* Search view for mobiles */}
      <TextField
        className="search-mobile"
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search color="primary" />
            </InputAdornment>
          ),
        }}
        placeholder="Search for items/categories"
        name="search"
        value={searchTerm}
        onChange={handleSearchTerm}
      />
      <Grid container>
        <Grid item xs={12} md={localStorage.getItem('token') ? 9:12}>
          <Grid item className="product-grid">
            <Box className="hero">
              <p className="hero-heading">
                India’s <span className="hero-highlight">FASTEST DELIVERY</span>{" "}
                to your door step
              </p>
            </Box>
          </Grid>
          <Grid item xs={12}>
            {loader ? <Grid style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
              <CircularProgress />
              <Typography>Loading Products</Typography>
            </Grid> : <Grid container>
              {products.length ? products.map((product, index) => {
                return <Grid key={`${product._id}-${index}`} item xs={6} md={3} sx={{ padding: '10px' }}>
                  <ProductCard product={product} handleAddToCart={handleAddToCart}/>
                </Grid>
              }) : <Grid item xs={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                <SentimentDissatisfied />
                <Typography>No products found</Typography>
              </Grid>}
            </Grid>}
          </Grid>
        </Grid>
        {localStorage.getItem('token') ?<Grid item xs={12} md={3}>
          <Cart products={products} items={items} handleQuantity={handleQuantity}/>
        </Grid>:''}
      </Grid>
      <Footer />
    </div>
  );
};

export default Products;
